import * as d3 from 'd3';
import chartTooltip from './d3-tooltip';
import { DURATION, LEGEND_ITEM_RADIUS, getSVGPosition } from '../utils';
import { labeler } from '../../utils';
import './d3-riskreturnchart.css';

d3.selection.prototype.moveToFront = function () {
  return this.each(function () {
    this.parentNode.appendChild(this);
  });
};

export default function () {
  const margin = { top: 10, right: 100, bottom: 60, left: 100 };
  const scaleOrdinal = d3.scaleOrdinal(d3.schemeCategory10);

  let width = 400;
  let height = 300;
  let label = { riskStatistic: 'Risk', Statistic: 'Return' }; // TODO: Rename. We're no longer using this for labels but for other functions
  let _data;
  let _selection;
  let svg;
  let tooltip;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let t;
  let xBaseline;
  let yBaseline;
  let statistics;
  let frontier = null;
  let asset;
  let classes;
  let labelPositions;
  let updateLabels = true;
  let returnDropdown;
  let riskDropdown;

  const dispatch = d3.dispatch('selected');

  function chart(selection, isResize) {
    selection.each(function (data) {
      // Check if data is different
      updateLabels = (JSON.stringify(data) === JSON.stringify(_data))
        && width === chartWidth + margin.left + margin.right
        && height === chartHeight + margin.top + margin.bottom
        ? false : true;

      t = d3.transition().duration(isResize ? 0 : DURATION);

      _data = data;
      _selection = selection;
      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');

        createDefs();

        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();
        createLegend();
        createGuideLines();

        g.append('g')
          .attr('class', 'rr-chart__frontier');

        g.append('g')
          .attr('class', 'rr-chart__points');

        createTooltip();
        createAxesSelectors();
        createBaselines();
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();
      drawFrontier();
      drawPoints();
      drawLegend();
      drawAxesSelectors();
      drawBaselines();
    });
  }

  function createDefs() {
    const defs = svg.append('defs');

    const dropShadowFilter = defs.append('svg:filter')
      .attr('id', 'drop-shadow')
      .attr('filterUnits', 'userSpaceOnUse')
      .attr('width', '250%')
      .attr('height', '250%');

    dropShadowFilter.append('svg:feGaussianBlur')
      .attr('in', 'SourceGraphic')
      .attr('stdDeviation', 4)
      .attr('result', 'blur-out');

    dropShadowFilter.append('svg:feOffset')
      .attr('in', 'color-out')
      .attr('dx', 0)
      .attr('dy', 0)
      .attr('result', 'the-shadow');

    dropShadowFilter.append('svg:feBlend')
      .attr('in', 'SourceGraphic')
      .attr('in2', 'the-shadow')
      .attr('mode', 'normal');
  }

  function createScales() {
    const paddingFraction = 0.2;

    xBaseline = statistics.filter(function (d) { return d.statistic === label.riskStatistic; })[0].baseline;

    const xHighGood = statistics.filter(function (d) { return d.statistic === label.riskStatistic; })[0].highGood;

    yBaseline = statistics.filter(function (d) { return d.statistic === label.returnStatistic; })[0].baseline;

    const yHighGood = statistics.filter(function (d) { return d.statistic === label.returnStatistic; })[0].highGood;

    const xExtent = d3.extent(_data, function (d) { return d.riskStatistic; });
    const xMin = d3.min([xExtent[0], xBaseline]);
    const xMax = d3.max([xExtent[1], xBaseline]);
    const xWidth = xMax - xMin;

    x = d3.scaleLinear()
      .rangeRound(!xHighGood ? [0, chartWidth] : [chartWidth, 0])
      .domain([xMin - xWidth * paddingFraction, xMax + xWidth * paddingFraction]).nice();

    const yExtent = d3.extent(_data, function (d) { return d.returnStatistic; });
    const yMin = d3.min([yExtent[0], yBaseline]);
    const yMax = d3.max([yExtent[1], yBaseline]);
    const yWidth = yMax - yMin;

    y = d3.scaleLinear()
      .rangeRound(yHighGood ? [chartHeight, 0] : [0, chartHeight])
      .domain([yMin - yWidth * paddingFraction, yMax + yWidth * paddingFraction]).nice();
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'rr-chart__axis rr-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'rr-chart__axis rr-chart__axis--y')
  }

  function createLegend() {
    svg.select('g')
      .append('g')
      .attr('class', 'rr-chart__legend')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'end')
      .attr('cursor', 'pointer');
  }

  function createAxesSelectors() {
    const create = function (_class, key, isReturn) {
      const dropdown = _selection.append('div')
        .attr('class', 'rr-chart__axes-selector rr-chart__axes-selector--' + _class)
        .style('position', 'absolute')

      dropdown.append('select')
        .attr('class', 'form-control')
        .on('change', function () {
          dispatch.call('selected', chart, key, this.value);
        })
        .selectAll('option')
        .data(statistics.filter(function (d) { return d.isReturn === isReturn; }))
        .enter().append('option')
        .attr('value', function (d) { return d.statistic; })
        .text(function (d) { return d.statistic; });

      dropdown.select('select').node().value = label[key];

      return dropdown;
    }

    returnDropdown = create('return', 'returnStatistic', true);    
    riskDropdown = create('risk', 'riskStatistic', false);
  }

  function createGuideLines() {
    svg.select('g')
      .append('line')
      .attr('class', 'rr-chart__guide rr-chart__guide--x')
      .attr('stroke', 'grey')
      .attr('stroke-width', 1)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');

    svg.select('g')
      .append('line')
      .attr('class', 'rr-chart__guide rr-chart__guide--y')
      .attr('stroke', 'grey')
      .attr('stroke-width', 1)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');
  }

  function createBaselines() {
    svg.select('g')
      .append('line')
      .attr('class', 'rr-chart__baseline rr-chart__baseline--x')
      .attr('y1', 0)
      .attr('stroke', 'grey')
      .attr('stroke-width', 1)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');

    svg.select('g')
      .append('line')
      .attr('class', 'rr-chart_baseline rr-chart__baseline--y')
      .attr('x1', 0)
      .attr('stroke', 'grey')
      .attr('stroke-width', 1)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');
  }

  function createTooltip() {
    tooltip = chartTooltip();
    const tooltipContainer = d3.select('body')
      .append('div')
      .attr('class', 'rr-chart__tooltip');

    tooltipContainer.datum([]).call(tooltip);
  }

  function drawFrontier() {
    if (frontier === null) return;

    const minY = y.domain()[0];
    const maxX = x.domain()[1];

    const data = frontier.filter(function (d) { return d.mean > minY; })
      .filter(function (d) { return d.std < maxX; });

    const frontierLine = svg.select('g.rr-chart__frontier')
      .selectAll('.rr-chart__frontier')
      .data([data]);

    const line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(function (d) { return x(d.std); })
      .y(function (d) { return y(d.mean); });

    frontierLine.enter().append('path')
      .attr('class', 'rr-chart__frontier')
      .attr('fill', 'none')
      .attr('stroke', '#444')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')

    frontierLine.attr('d', line)
      .attr('opacity', label.riskStatistic === 'std' && label.returnStatistic === 'mean' ? 1 : 0);
  }

  function drawAxes() {
    svg.select('.rr-chart__axis--x')
      .attr('transform', 'translate(0,' + chartHeight + ')')
      .transition(t)
      .call(d3.axisBottom(x));

    svg.select('.rr-chart__axis--y')
      .transition(t)
      .call(d3.axisLeft(y));
  }

  function drawPoints() {
    const filteredData = _data.filter(function (d) {
      return d.riskStatistic !== 'NaN';
    });

    const point = svg.select('.rr-chart__points').selectAll('g')
      .data(filteredData, function (d) { return d.asset; });

    const color = scaleOrdinal.domain(_data.map(function (d) { return d.class; }));

    const pointEnter = point.enter().append('g')
      .attr('class', 'rr-chart__point')
      .on('click', function (d) {
        var sel = d3.select(this);
        sel.moveToFront();
        dispatch.call('selected', chart, 'asset', d.asset);
      })
      .on('mouseover', function () {
        tooltip.show();
        d3.selectAll('.rr-chart__guide').attr('display', 'block');
      })
      .on('mousemove', function (d) {
        const coords = d3.mouse(this);
        const SVGPos = getSVGPosition(this);
        tooltip.update({ x: coords[0] + SVGPos[0], y: coords[1] + SVGPos[1] }, d.statistics.map(function (x) { return { key: x.key, value: d3.format(".1f")(x.value) }; }), d.asset);
        drawGuideLines(d);
      })
      .on('mouseout', function () {
        tooltip.hide();
        d3.selectAll('.rr-chart__guide').attr('display', 'none');
      });

    pointEnter.append('circle')
      .attr('r', 6)
      .attr('cx', function (d) { return x(d.riskStatistic); })
      .attr('cy', function (d) { return y(d.returnStatistic); })
      .attr('fill', function (d) { return color(d.class); })
      .attr('opacity', 0.6);

    const calculateLabelPositions = function () {
      const labels = _data.map(function (d) {
        return {
          x: x(d.riskStatistic),
          y: y(d.returnStatistic),
          name: d.asset,
          width: d.asset.length * 8,
          height: 12,
        };
      });

      const anchors = _data.map(function (d) {
        return {
          x: x(d.riskStatistic),
          y: y(d.returnStatistic),
          r: 10,
        };
      });

      const l = labeler()
        .label(labels)
        .anchor(anchors)
        .width(chartWidth)
        .height(chartHeight);

      l.start(1000);

      return l.label().map(function (d) { return { x: d.x, y: d.y, label: d.name }; });
    }

    if (updateLabels) {
      labelPositions = calculateLabelPositions();
    }

    pointEnter.append('text')
      .text(function (d) { return d.asset; })
      .attr('text-anchor', 'start')
      .attr('font-size', '10px')
      .attr('x', function (d, i) { return labelPositions.filter(function (l) { return l.label === d.asset; })[0].x; })
      .attr('y', function (d, i) { return labelPositions.filter(function (l) { return l.label === d.asset; })[0].y; });

    point.classed('rr-chart__point--selected', function (d) { return d.asset === asset; })
      .classed('rr-chart__point--hidden', function (d) { return classes.has(d.class); });

    point.select('circle')
      .transition(t)
      .delay(function (d, i) { return 10 * i; })
      .attr('cx', function (d) { return x(d.riskStatistic); })
      .attr('cy', function (d) { return y(d.returnStatistic); });

    point.select('text')
      .transition(t)
      .delay(function (d, i) { return 10 * i; })
      .attr('x', function (d, i) { return labelPositions.filter(function (l) { return l.label === d.asset; })[0].x; })
      .attr('y', function (d, i) { return labelPositions.filter(function (l) { return l.label === d.asset; })[0].y; });

    point.exit().remove();
  }

  function drawLegend() {
    const legend = svg.select('.rr-chart__legend')
      .selectAll('g')
      .data(d3.map(_data, function (d) { return d.class; }).keys());

    const color = scaleOrdinal;

    const legendEnter = legend.enter().append('g')
      .attr('transform', function (d, i) { return 'translate(0,' + i * 20 + ')'; })
      .on('click', function (d) {
        const newClasses = new Set(classes);
        classes.has(d) ? newClasses.delete(d) : newClasses.add(d);
        dispatch.call('selected', chart, 'classes', newClasses);
      });

    legend.classed('rr-chart__legend-element--hidden', function (d) {
      return classes.has(d);
    });

    legendEnter.append('circle')
      .attr('cx', 10)
      .attr('cy', 9)
      .attr('r', LEGEND_ITEM_RADIUS)
      .attr('stroke', 'black')
      .attr('stroke-width', 1)
      .merge(legend.select('circle'))
      .attr('cx', chartWidth + 86)
      .attr('fill', function (d) { return color(d); });

    legendEnter.append('text')
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .attr('fill', 'black')
      .merge(legend.select('text'))
      .attr('x', chartWidth + 75)
      .text(function (d) { return d; });
  }

  function drawAxesSelectors() {
    // TODO: Extract magic constants
    riskDropdown.style('left', margin.left + chartWidth / 2 - 10 + 'px') // Note that select inputs are wider on mobile
      .style('top', height - 25 + 'px');

    riskDropdown.select('select')
      .node().value = label.riskStatistic;

    returnDropdown.style('left', 5 + 'px')
      .style('top', margin.top + chartHeight / 2 + 'px');

    returnDropdown.select('select')
      .node().value = label.returnStatistic;
  }

  function drawGuideLines(d) {
    svg.select('.rr-chart__guide--x')
      .attr('x1', x(d.riskStatistic))
      .attr('y1', chartHeight)
      .attr('x2', x(d.riskStatistic))
      .attr('y2', y(d.returnStatistic));

    svg.select('.rr-chart__guide--y')
      .attr('x1', 0)
      .attr('y1', y(d.returnStatistic))
      .attr('x2', x(d.riskStatistic))
      .attr('y2', y(d.returnStatistic));
  }

  function drawBaselines() {
    svg.select('.rr-chart__baseline--x')
      .transition(t)
      .attr('x1', x(xBaseline))
      .attr('x2', x(xBaseline))
      .attr('y2', chartHeight);

    svg.select('.rr-chart__baseline--y')
      .transition(t)
      .attr('y1', y(yBaseline))
      .attr('x2', chartWidth)
      .attr('y2', y(yBaseline));
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.label = function (value) {
    if (!arguments.length) return label;
    label = value;
    return chart;
  }

  chart.statistics = function (value) {
    if (!arguments.length) return statistics;
    statistics = value;
    return chart;
  }

  chart.frontier = function (value) {
    if (!arguments.length) return frontier;
    frontier = value;
    return chart;
  }

  chart.asset = function (value) {
    if (!arguments.length) return asset;
    asset = value;
    return chart;
  }

  chart.classes = function (value) {
    if (!arguments.length) return classes;
    classes = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  chart.cleanup = function () {
    d3.select('.rr-chart__tooltip').remove();
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-riskreturnchart.js